import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './Activities.css';
import { supabase } from '../../lib/supabase';

const Activities = () => {
    const navigate = useNavigate();
    const [activities, setActivities] = useState([]);
    const [activitiesLoading, setActivitiesLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [showClearConfirm, setShowClearConfirm] = useState(false);
    const [showClearMenu, setShowClearMenu] = useState(false);
    const [clearOption, setClearOption] = useState(null);
    const [customDate, setCustomDate] = useState('');
    const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [showDeleteItemConfirm, setShowDeleteItemConfirm] = useState(false);

    const observerRef = useRef(null);
    const loadMoreRef = useRef(null);
    const PAGE_SIZE = 50;

    // Initial fetch
    useEffect(() => {
        fetchActivities(true);
    }, []);

    // Close clear menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showClearMenu && !event.target.closest('.activities-clear-wrapper')) {
                setShowClearMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showClearMenu]);

    // Infinite scroll observer
    useEffect(() => {
        if (activitiesLoading) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loadingMore) {
                    fetchActivities(false);
                }
            },
            { threshold: 0.1 }
        );

        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }

        observerRef.current = observer;

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [activitiesLoading, hasMore, loadingMore, activities.length]);

    const fetchActivities = async (isInitial = false) => {
        try {
            if (isInitial) {
                setActivitiesLoading(true);
                setActivities([]);
            } else {
                setLoadingMore(true);
            }

            const offset = isInitial ? 0 : activities.length;

            const { data, error } = await supabase
                .from('cms_activities')
                .select('*')
                .is('school_id', null)
                .order('added_at', { ascending: false })
                .range(offset, offset + PAGE_SIZE - 1);

            if (error) {} else {
                if (isInitial) {
                    setActivities(data || []);
                } else {
                    setActivities(prev => [...prev, ...(data || [])]);
                }
                setHasMore((data || []).length === PAGE_SIZE);
            }
        } catch (err) {
            } finally {
            setActivitiesLoading(false);
            setLoadingMore(false);
        }
    };

    const handleClearActivities = async (option = 'all') => {
        try {
            let query = supabase.from('cms_activities').delete().is('school_id', null);

            if (option === 'today') {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                query = query.gte('added_at', today.toISOString());
            } else if (option === 'week') {
                const weekAgo = new Date();
                weekAgo.setDate(weekAgo.getDate() - 7);
                weekAgo.setHours(0, 0, 0, 0);
                query = query.gte('added_at', weekAgo.toISOString());
            } else if (option === 'month') {
                const monthAgo = new Date();
                monthAgo.setMonth(monthAgo.getMonth() - 1);
                monthAgo.setHours(0, 0, 0, 0);
                query = query.gte('added_at', monthAgo.toISOString());
            } else if (option === 'custom' && customDate) {
                const customDateObj = new Date(customDate);
                customDateObj.setHours(0, 0, 0, 0);
                query = query.gte('added_at', customDateObj.toISOString());
            }

            const { error } = await query;

            if (error) {alert('Error clearing activities: ' + error.message);
            } else {
                await fetchActivities(true);
                setShowClearConfirm(false);
                setShowClearMenu(false);
                setClearOption(null);
                setCustomDate('');
                setShowCustomDatePicker(false);
            }
        } catch (err) {alert('Error clearing activities: ' + err.message);
        }
    };

    const handleDeleteActivity = async (activityId) => {
        try {
            setActivities(prev => prev.filter(activity => activity.id !== activityId));

            const { error } = await supabase
                .from('cms_activities')
                .delete()
                .eq('id', activityId);

            if (error) {await fetchActivities(true);
                alert('Error deleting activity: ' + error.message);
            }

            setShowDeleteItemConfirm(false);
            setDeleteItemId(null);
        } catch (err) {await fetchActivities(true);
            alert('Error deleting activity: ' + err.message);
            setShowDeleteItemConfirm(false);
            setDeleteItemId(null);
        }
    };

    const handleClearOptionClick = (option) => {
        setShowClearMenu(false);
        if (option === 'custom') {
            setClearOption('custom');
            setShowCustomDatePicker(true);
            setShowClearConfirm(true);
        } else {
            setClearOption(option);
            setShowCustomDatePicker(false);
            setShowClearConfirm(true);
        }
    };

    const formatTimeAgo = (timestamp) => {
        const now = new Date();
        const activityTime = new Date(timestamp);
        const diffInSeconds = Math.floor((now - activityTime) / 1000);

        if (diffInSeconds < 60) {
            return 'Just now';
        } else if (diffInSeconds < 3600) {
            const minutes = Math.floor(diffInSeconds / 60);
            return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
        } else if (diffInSeconds < 86400) {
            const hours = Math.floor(diffInSeconds / 3600);
            return `${hours} hour${hours > 1 ? 's' : ''} ago`;
        } else {
            const days = Math.floor(diffInSeconds / 86400);
            return `${days} day${days > 1 ? 's' : ''} ago`;
        }
    };

    const getActivityIcon = (title) => {
        if (title.toLowerCase().includes('school')) {
            return (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
                </svg>
            );
        } else if (title.toLowerCase().includes('user') || title.toLowerCase().includes('student')) {
            return (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" />
                </svg>
            );
        } else if (title.toLowerCase().includes('provider')) {
            return (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM17 12H7V10H17V12ZM15 16H7V14H15V16ZM17 8H7V6H17V8Z" />
                </svg>
            );
        } else {
            return (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
                </svg>
            );
        }
    };

    const getClearOptionLabel = () => {
        switch (clearOption) {
            case 'today': return 'today\'s activities';
            case 'week': return 'activities from the last week';
            case 'month': return 'activities from the last month';
            case 'custom': return `activities from ${customDate}`;
            case 'all': return 'all activities';
            default: return 'activities';
        }
    };

    return (
        <div className="activities-page">
            <div className="activities-container">
                {/* Header */}
                <div className="activities-header">
                    <div className="activities-header-left">
                        <button className="activities-back-btn" onClick={() => navigate('/dashboard')}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                            </svg>
                            Back to Dashboard
                        </button>
                        {/* <h1>All Activities</h1> */}
                    </div>
                    <div className="activities-clear-wrapper" style={{ position: 'relative' }}>
                        <button
                            className="activities-clear-btn"
                            onClick={() => setShowClearMenu(!showClearMenu)}
                            disabled={activities.length === 0}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                            </svg>
                            Clear
                        </button>
                        {showClearMenu && (
                            <div className="activities-clear-menu">
                                <button onClick={() => handleClearOptionClick('today')}>Clear All from Today</button>
                                <button onClick={() => handleClearOptionClick('week')}>Clear All from last week</button>
                                <button onClick={() => handleClearOptionClick('month')}>Clear All from last month</button>
                                <button onClick={() => handleClearOptionClick('custom')}>Custom Date</button>
                                <button onClick={() => handleClearOptionClick('all')}>Clear All</button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Activity List */}
                <div className="activities-content">
                    {activitiesLoading ? (
                        <div className="activities-loading">
                            <div className="activities-spinner">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" />
                                </svg>
                            </div>
                            <p>Loading activities...</p>
                        </div>
                    ) : activities.length === 0 ? (
                        <div className="activities-empty">
                            <div className="activities-empty-icon">
                                <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                                </svg>
                            </div>
                            <h3>No Activities</h3>
                            <p>Activities will appear here when you add schools, users, or providers.</p>
                        </div>
                    ) : (
                        <div className="activities-list">
                            {activities.map((activity) => (
                                <div key={activity.id} className="activities-item">
                                    <div className="activities-item-icon">
                                        {getActivityIcon(activity.title)}
                                    </div>
                                    <div className="activities-item-content">
                                        <p><strong>{activity.title}</strong></p>
                                        {activity.details && <p className="activities-item-details">{activity.details}</p>}
                                        <span className="activities-item-time">{formatTimeAgo(activity.added_at)}</span>
                                    </div>
                                    <button
                                        className="activities-item-delete-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDeleteItemId(activity.id);
                                            setShowDeleteItemConfirm(true);
                                        }}
                                        title="Delete activity"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                                        </svg>
                                    </button>
                                </div>
                            ))}

                            {/* Load more trigger */}
                            <div ref={loadMoreRef} className="activities-load-more">
                                {loadingMore && (
                                    <div className="activities-loading-more">
                                        <div className="activities-spinner small">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" />
                                            </svg>
                                        </div>
                                        <span>Loading more...</span>
                                    </div>
                                )}
                                {!hasMore && activities.length > 0 && (
                                    <div className="activities-end">
                                        <span>No more activities to load</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Clear Confirmation Modal */}
            {showClearConfirm && (
                <div className="activities-modal-overlay" onClick={() => setShowClearConfirm(false)}>
                    <div className="activities-confirmation-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="activities-confirmation-header">
                            <div className="activities-confirmation-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                                </svg>
                            </div>
                            <h2>Clear Activities</h2>
                        </div>
                        <div className="activities-confirmation-content">
                            {showCustomDatePicker ? (
                                <div className="activities-custom-date-container">
                                    <p className="activities-confirmation-message">Select a date to clear all activities from:</p>
                                    <input
                                        type="date"
                                        className="activities-custom-date-input"
                                        value={customDate}
                                        onChange={(e) => setCustomDate(e.target.value)}
                                    />
                                </div>
                            ) : (
                                <p className="activities-confirmation-message">
                                    Are you sure you want to clear {getClearOptionLabel()}? This action cannot be undone.
                                </p>
                            )}
                        </div>
                        <div className="activities-confirmation-actions">
                            <button
                                className="activities-cancel-btn"
                                onClick={() => {
                                    setShowClearConfirm(false);
                                    setClearOption(null);
                                    setCustomDate('');
                                    setShowCustomDatePicker(false);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="activities-confirm-btn"
                                onClick={() => handleClearActivities(clearOption)}
                                disabled={showCustomDatePicker && !customDate}
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Single Item Confirmation */}
            {showDeleteItemConfirm && (
                <div className="activities-modal-overlay" onClick={() => setShowDeleteItemConfirm(false)}>
                    <div className="activities-confirmation-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="activities-confirmation-header">
                            <div className="activities-confirmation-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                                </svg>
                            </div>
                            <h2>Delete Activity</h2>
                        </div>
                        <div className="activities-confirmation-content">
                            <p className="activities-confirmation-message">
                                Are you sure you want to delete this activity? This action cannot be undone.
                            </p>
                        </div>
                        <div className="activities-confirmation-actions">
                            <button
                                className="activities-cancel-btn"
                                onClick={() => {
                                    setShowDeleteItemConfirm(false);
                                    setDeleteItemId(null);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="activities-confirm-btn"
                                onClick={() => handleDeleteActivity(deleteItemId)}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Activities;
